import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Spacer } from "../editor/EditorHelpers";
import { Button } from "../components/Button";
import { FiArrowUpRight, FiChevronDown, FiChevronUp } from "react-icons/fi";
import styled from "styled-components";
import Checkbox from "../components/Checkbox";
import { minutes } from "../util/timeUtil";
import deepCopy from "../util/deepCopy";
import { TextEditor } from "../components/TextEditor";
import { KEY_OWN_CID, KEY_TOKEN } from "./challengeModel";
import { ProgressSection, simplifyProgress } from "./progressModel";
import { SmallText } from "./ChallengeHelpers";



interface RegisterProgressProps {
  storedProgress: any;  
  refresh: () => void;
  openWithSection?: ProgressSection;
}

const mergeProgressWithChunk = (progress: Progress, chunk: RegistrationChunk) => {
  if (!chunk) {
    return progress;
  }
  if (!progress || typeof progress !== 'object') {
    const newProgress: Progress = {
      registrationChunks: [chunk]
    }
    return newProgress;
  }
  const merged = deepCopy(progress);
  if (!merged.registrationChunks) {
    merged.registrationChunks = [];
  }
  const existingChunk = getCurrentRegistrationChunk(merged);
  if (existingChunk) {
    Object.entries(chunk.changes).forEach(([key, value]) => {
      existingChunk.changes[key] = value;
    });
  } else {
    merged.registrationChunks.push(chunk);
  }
  return merged;
}

const doStoreNewProgress = (progress, refresh) => {
  const formData: Record<string, any> = {
    own_cid: localStorage.getItem(KEY_OWN_CID),
    token: localStorage.getItem(KEY_TOKEN),
    progress: JSON.stringify(progress),
  };
  console.log("doStoreNewProgress:", simplifyProgress(progress));
  fetch('/api/update_challenge.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: new URLSearchParams(formData)
  })
    .then(response => response.json())
    .then(data => {
      if (data.status) {
        console.log("update_challenge.php status:", data.status);
        refresh();
      } else {
        console.log("update_challenge.php error:", data.error);
      }
    })
    .catch(error => console.error('Error:', error));

}


export const RegisterProgress: React.FC<RegisterProgressProps> = ({ storedProgress, refresh, openWithSection }) => {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState<ProgressSection | "">(openWithSection || "");

  const [progress, setProgress] = useState<Progress | null>(storedProgress);
  const progressRef = useRef(progress);
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    console.log("openSection:", openSection);
  }, [openSection]);

  const [unsavedChunk, setUnsavedChunk] = useState<RegistrationChunk | null>(null);
  const unsavedChunkRef = useRef(unsavedChunk);
  useEffect(() => {
    unsavedChunkRef.current = unsavedChunk;
    setProgress(mergeProgressWithChunk(storedProgress, unsavedChunk));
  }, [unsavedChunk]);

  useEffect(() => {
    return () => {
      console.log("cleanup on unmount:", unsavedChunkRef.current);
      if (unsavedChunkRef.current) {
        doStoreNewProgress(progressRef.current, refresh);
      }
    };
  }, []);

  useEffect(() => {
    console.log("storedProgress changed:", simplifyProgress(storedProgress));
    // Don't really expect changes from other sources, but it doesn't hurt to merge
    setProgress(mergeProgressWithChunk(storedProgress, unsavedChunk));
    // Purge unsavedChunk
    setUnsavedChunk((prev) => {
      if (!prev) {
        return prev;
      }
      const entriesToKeep = Object.entries(prev.changes).filter(([key, value]) => {
        return getProgressPrimitive(storedProgress, key) !== value;
      });
      const purgeCount = Object.keys(prev.changes).length - entriesToKeep.length;
      console.log("storedProgress purge count:", purgeCount);
      if (entriesToKeep.length > 0) {
        return {
          startTimeMs: prev.startTimeMs,
          changes: Object.fromEntries(entriesToKeep)
        }
      }
      return null;
    });
  }, [storedProgress]);

  // Debounce changes
  useEffect(() => {
    if (!unsavedChunk) {
      return;
    }
    const handler = setTimeout(() => {
      doStoreNewProgress(progress, refresh);
    }, 10000); // TODO decrease

    // Cleanup function to clear the timeout
    return () => {
      clearTimeout(handler);
    };
  }, [progress]);

  const toggleOpen = (sectionName: ProgressSection) => {
    if (openSection === sectionName) {
      setOpenSection("");
    } else {
      setOpenSection(sectionName);
    }
  }

  const handleChange = (key: string, value: any) => {
    setUnsavedChunk((prev) => {
      const chunk = prev
        ? deepCopy(prev)
        : {
          startTimeMs: Date.now(),
          changes: {}
        };
      chunk.changes[key] = value;
      return chunk;
    });
  }

  return (
    <>
      <AccordionSection title={t('challenge.task.reflect-on-values')} isOpen={openSection === ProgressSection.VALUES} onToggleOpen={() => toggleOpen(ProgressSection.VALUES)}>
        <AccordionSectionContainer><ReflectOnValues progress={progress} onChange={handleChange} /></AccordionSectionContainer>
      </AccordionSection>
      <AccordionSection title={t('challenge.task.learn-about-alternatives')} isOpen={openSection === ProgressSection.LEARN} onToggleOpen={() => toggleOpen(ProgressSection.LEARN)}>
        <AccordionSectionContainer><LearnAboutAlternatives progress={progress} onChange={handleChange} /></AccordionSectionContainer>
      </AccordionSection>
      <AccordionSection title={t('challenge.task.change-purchasing-habits')} isOpen={openSection === ProgressSection.PURCHASES} onToggleOpen={() => toggleOpen(ProgressSection.PURCHASES)}>
        <AccordionSectionContainer><ChangePurchasingHabits progress={progress} onChange={handleChange} /></AccordionSectionContainer>
      </AccordionSection>
      <AccordionSection title={t('challenge.task.reconsider-online-services')} isOpen={openSection === ProgressSection.SERVICES} onToggleOpen={() => toggleOpen(ProgressSection.SERVICES)}>
        <AccordionSectionContainer><ReconsiderOnlineServices progress={progress} onChange={handleChange} /></AccordionSectionContainer>
      </AccordionSection>
      <AccordionSection title={t('challenge.task.spread-the-word')} isOpen={openSection === ProgressSection.SPREAD_THE_WORD} onToggleOpen={() => toggleOpen(ProgressSection.SPREAD_THE_WORD)}>
        <AccordionSectionContainer><SpreadTheWord progress={progress} onChange={handleChange} /></AccordionSectionContainer>
      </AccordionSection>
      <AccordionSection title={t('challenge.task.share-the-challenge')} isOpen={openSection === ProgressSection.SHARE} onToggleOpen={() => toggleOpen(ProgressSection.SHARE)}>
        <AccordionSectionContainer><ShareChallenge progress={progress} onChange={handleChange} /></AccordionSectionContainer>
      </AccordionSection>
    </>
  )
}

interface ProgressSectionProps {
  progress: Progress;
  onChange: (key: string, value: any) => void;
}

export const getValueKeysAndLabels = (t) => {
  /*
    When deciding which values to include in the checkbox,
    we go through them in the order of this list and include
    as many as there are room for.

    When choosing which order to display the ones there are
    room for however, the items are sorted by the third value
    here. This prevents weirdness such as "resisting trump and worker rights"
  */
  return [
    ["challenge.value.democracy", t('challenge.value.democracy'), 0],
    ["challenge.value.stronger-europe", t('challenge.value.stronger-europe'), 0],
    ["challenge.value.sustainable-future", t('challenge.value.sustainable-future'), 0],
    ["challenge.value.resist-trump", t('challenge.value.resist-trump'), 2],
    ["challenge.value.environment", t('challenge.value.environment'), 0],
    ["challenge.value.slava-ukraina", t('challenge.value.slava-ukraina'), 1],
    ["challenge.value.anti-genocide", t('challenge.value.anti-genocide'), 1],
    ["challenge.value.anti-big-tech", t('challenge.value.anti-big-tech'), 1],
    ["challenge.value.fight-oligarchy", t('challenge.value.fight-oligarchy'), 2],
    ["challenge.value.support-local", t('challenge.value.support-local'), 2],
    ["challenge.value.worker-rights", t('challenge.value.worker-rights'), 0],
    ["challenge.value.economic-growth", t('challenge.value.economic-growth'), 0],
  ];
}

const ReflectOnValues: React.FC<ProgressSectionProps> = ({ progress, onChange }) => {
  const { t } = useTranslation();
  const [valueKeysAndLabels] = useState(getValueKeysAndLabels(t));

  return (
    <>    
      <h3>{t('challenge.register.reflect-on-values.header')}</h3>
      {valueKeysAndLabels.map(([key, label]) => <ValueCheckBox key={key} progress={progress} onChange={onChange} storeKey={key} label={label} />)}
      <Spacer />
      <Label>{t('custom.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.value.custom") as string || ""} onChange={(v) => onChange("challenge.value.custom", v)}
      />
      <p>{t('challenge.register.reflect-on-values.text')}</p>
    </>
  )
}

interface ValueCheckBoxProps {
  progress: any;
  onChange: (key: string, value: any) => void;
  storeKey: string;
  label: string;
}

const ValueCheckBox: React.FC<ValueCheckBoxProps> = ({ progress, onChange, storeKey, label }) => {
  const checked = !!getProgressPrimitive(progress, storeKey)
  return (
    <>
      <Spacer $height="2px" />
      <Checkbox
        id={storeKey}
        checked={checked}
        onChange={() => onChange(storeKey, !checked)}
        label={label}
        small
      />
    </>
  )
}

export const getSpreadKeysAndLabels = (t) => {
  /*
    When deciding which values to include in the checkbox,
    we go through them in the order of this list and include
    as many as there are room for.

    When choosing which order to display the ones there are
    room for however, the items are sorted by the third value
    here. This prevents weirdness such as "resisting trump and worker rights"
  */
  return [
    ["challenge.spread-the-word.talked-to-friends", t('challenge.spread-the-word.talked-to-friends'), 0],
    ["challenge.spread-the-word.posted-online", t('challenge.spread-the-word.posted-online'), 0],
    ["challenge.spread-the-word.discussed-at-work", t('challenge.spread-the-word.discussed-at-work'), 0],
    ["challenge.spread-the-word.attended-event", t('challenge.spread-the-word.attended-event'), 0],
    ["challenge.spread-the-word.distributed-print-outs", t('challenge.spread-the-word.distributed-print-outs'), 0],
    ["challenge.spread-the-word.been-politically-involved", t('challenge.spread-the-word.been-politically-involved'), 0],
  ];
}

const SpreadTheWord: React.FC<ProgressSectionProps> = ({ progress, onChange }) => {
  const { t } = useTranslation();
  const [spreadKeysAndLabels] = useState(getSpreadKeysAndLabels(t));

  return (
    <>    
      <h3>{t('challenge.register.spread-the-word.header')}</h3>
      {spreadKeysAndLabels.map(([key, label]) => <ValueCheckBox key={key} progress={progress} onChange={onChange} storeKey={key} label={label} />)}
      <Spacer />
      <Label>{t('custom.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.spread-the-word.custom") as string || ""} onChange={(v) => onChange("challenge.spread-the-word.custom", v)}
      />
      <p>{t('challenge.register.spread-the-word.text')}</p>
    </>
  )
}

export const getShareKeysAndLabels = (t) => {
  /*
    When deciding which values to include in the checkbox,
    we go through them in the order of this list and include
    as many as there are room for.

    When choosing which order to display the ones there are
    room for however, the items are sorted by the third value
    here. This prevents weirdness such as "resisting trump and worker rights"
  */
  return [
    ["challenge.share.mastodon", "Mastodon", 0],
    ["challenge.share.bluesky", "Bluesky", 0],
    ["challenge.share.direct-message", t('challenge.share.direct-message'), 0],
    ["challenge.share.facebook", "Facebook", 0],
    ["challenge.share.instagram", "Instagram", 0],
    ["challenge.share.twitter", "Twitter", 0],
    ["challenge.share.revolt", "Revolt", 0],
    ["challenge.share.discord", "Discord", 0],
    ["challenge.share.lemmy", "Lemmy", 0],
    ["challenge.share.reddit", "Reddit", 0],
  ];
}

const ShareChallenge: React.FC<ProgressSectionProps> = ({ progress, onChange }) => {
  const { t } = useTranslation();
  const [spreadKeysAndLabels] = useState(getShareKeysAndLabels(t));

  return (
    <>    
      <h3>{t('challenge.register.share.header')}</h3>
      {spreadKeysAndLabels.map(([key, label]) => <ValueCheckBox key={key} progress={progress} onChange={onChange} storeKey={key} label={label} />)}
      <Spacer />
      <Label>{t('custom.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.share.custom") as string || ""} onChange={(v) => onChange("challenge.share.custom", v)}
      />
      <p>{t('challenge.register.share.text')}</p>
    </>
  )
}

const LearnAboutAlternatives: React.FC<ProgressSectionProps> = ({ progress, onChange }) => {
  const { t } = useTranslation();

  return (
    <>    
      <h3>{t('challenge.register.learn.header')}</h3>
      <LearnAnchorDiv>{t('challenge.register.learn.hardno.link.label')} <a href="https://hardno.eu/pick-language/europe" target="_blank">hardno.eu<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></LearnAnchorDiv>
      <LearnAnchorDiv>{t('challenge.register.learn.directories.link.label')} 
        <div><a href="https://buy-european.net" target="_blank">buy-european.net<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
        <div><a href="https://goeuropean.org" target="_blank">goeuropean.org<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
        <div><a href="https://european-alternatives.eu" target="_blank">european-alternatives.eu&nbsp;<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
      </LearnAnchorDiv>
      <LearnAnchorDiv>{t('challenge.register.learn.forums.link.label')} 
        <div><a href="https://lemmy.world/c/buyeuropean@feddit.uk" target="_blank">buyeuropean@feddit.uk<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
        <div><a href="https://www.reddit.com/r/BuyFromEU/" target="_blank">r/BuyFromEU<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
        <div><a href="https://www.reddit.com/r/BuyEUandCanadian/" target="_blank">r/BuyEUandCanadian<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
        <div><a href="https://www.reddit.com/r/BuyEuropean/" target="_blank">r/BuyEuropean<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
      </LearnAnchorDiv>
      <LearnAnchorDiv>{t('challenge.register.learn.leave-russia.link.label')} 
        <div><a href="https://leave-russia.org" target="_blank">leave-russia.org<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
      </LearnAnchorDiv>
      <LearnAnchorDiv>{t('challenge.register.learn.search.link.label')}
        <div><a href="https://qwant.com" target="_blank">qwant.com<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
        <div><a href="https://wikipedia.org" target="_blank">wikipedia.org<FiArrowUpRight size={16} style={{ verticalAlign: 'middle' }} /></a></div>
      </LearnAnchorDiv>
      <Spacer $height="1rem" />
      <Label>{t('challenge.register.learn.european.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.learn.european") as string || ""} onChange={(v) => onChange("challenge.learn.european", v)}
      />
      <SmallText>{t('challenge.register.learn.european.example')}</SmallText>
      <Spacer $height="0.5rem" />
      <Label>{t('challenge.register.learn.not-european.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.learn.not-european") as string || ""} onChange={(v) => onChange("challenge.learn.not-european", v)}
      />
      <SmallText>{t('challenge.register.learn.not-european.example')}</SmallText>
    </>
  )
}

const LearnAnchorDiv = styled.div`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`

const ChangePurchasingHabits: React.FC<ProgressSectionProps> = ({ progress, onChange }) => {
  const { t } = useTranslation();

  return (
    <>    
      <h3>{t('challenge.register.change-purchasing.header')}</h3>
      <Spacer $height="0.5rem" />
      <Label>{t('challenge.register.change-purchasing.started.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.change-purchasing.started") as string || ""} onChange={(v) => onChange("challenge.change-purchasing.started", v)}
      />
      <SmallText>{t('challenge.register.learn.european.example')}</SmallText>
      <Spacer $height="0.5rem" />
      <Label>{t('challenge.register.change-purchasing.stopped.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.change-purchasing.stopped") as string || ""} onChange={(v) => onChange("challenge.change-purchasing.stopped", v)}
      />
      <SmallText>{t('challenge.register.learn.not-european.example')}</SmallText>
    </>
  )
}

// challenge.reconsider-services.signed-up-with
const ReconsiderOnlineServices: React.FC<ProgressSectionProps> = ({ progress, onChange }) => {
  const { t } = useTranslation();

  return (
    <>    
      <h3>{t('challenge.register.reconsider-services.header')}</h3>
      <Spacer $height="0.5rem" />
      <Label>{t('challenge.register.reconsider-services.signed-up-with.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.reconsider-services.signed-up-with") as string || ""} onChange={(v) => onChange("challenge.reconsider-services.signed-up-with", v)}
      />
      <Spacer $height="0.5rem" />
      <Label>{t('challenge.register.reconsider-services.reduced-or-cancelled.label')}</Label>
      <TextEditor
        value={getProgressPrimitive(progress, "challenge.reconsider-services.reduced-or-cancelled") as string || ""} onChange={(v) => onChange("challenge.reconsider-services.reduced-or-cancelled", v)}
      />
      {t('challenge.register.reconsider-services.text').split('\n\n').map((p) => <p>{p}</p>)}
      </>
  )
}


const chunkPeriod = minutes(60);

// Define another class that uses RegistrationChunk
export interface Progress {
  registrationChunks: RegistrationChunk[];
}

interface RegistrationChunk {
  startTimeMs: number;
  changes: Record<string, any>;
}


export const getProgressPrimitive = (progress: Progress, key: string): boolean | string | number => {
  if (!progress || !progress.registrationChunks) {
    return undefined;
  }
  // Sort registrationChunks by startTimeMs in descending order
  const sortedChunks = progress.registrationChunks.sort((a, b) => b.startTimeMs - a.startTimeMs);

  // Iterate over the sorted registrationChunks
  for (const chunk of sortedChunks) {
    if (key in chunk.changes) {
      return chunk.changes[key];
    }
  }
  return undefined;
}

const getCurrentRegistrationChunk = (progress: Progress): RegistrationChunk => {
  const now = Date.now()
  for (let chunk of progress.registrationChunks) {
    if (now - chunk.startTimeMs < chunkPeriod) {
      return chunk;
    }
  }
  return undefined;
}



interface AccordionSectionProps {
  title: string;
  children: ReactNode;
  isOpen: boolean;
  onToggleOpen: () => void;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title, children, isOpen, onToggleOpen
}) => {
  return (
    <div>
      <AccordionHeaderContainer onClick={onToggleOpen}>
        {title}
        <Spacer style={{flexGrow: 1}} />
        <Button $borderless onClick={onToggleOpen} style={{ color: 'var(--text-color)'}}>
          {isOpen
            ? <FiChevronUp size={18} style={{ verticalAlign: 'middle' }} />
            : <FiChevronDown size={18} style={{ verticalAlign: 'middle' }} />
          }
        </Button>
      </AccordionHeaderContainer>
      {isOpen && children}
    </div>
  );
}

export const AccordionHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  margin: 0.75rem 1.5rem 0.75rem 2rem;
  cursor: pointer;
  font-weight: 600;
`

const AccordionSectionContainer = styled.div`
  background-color: rgba(16, 16, 16, 0.25);
  padding: 0.75rem 1.5rem 0.75rem 2rem;
`

